"use client"

import { ComponentConfig } from "@measured/puck"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import { twMerge } from "tailwind-merge"
import { generateImagePath } from "../../../../../../utils/generateImagePath"
import { DefaultImage, fetchPostByCategories } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import { TagData } from "../../../types"

export interface ArticleItem {
  image1?: string
  image2?: string
  time?: Date
  itemTitle?: string
  excerpt?: string
  tags?: TagData[]
}

export interface ArticlesProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  gapCustom?: string
  heightContact?: string
  category?: string[]
  articleItem: ArticleItem[]
}

export const RenderConfig: ComponentConfig<ArticlesProps> = {
  render: ({ className, category, styles, gapCustom, responsiveType, articleItem }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const [articleItems, setArticleItems] = useState<ArticleItem[]>([])
    const [filteredArticles, setFilteredArticles] = useState<ArticleItem[]>([])
    const [tags, setTags] = useState<string[]>([])
    const [activeTag, setActiveTag] = useState<string>("All")
    const fetchPostByCategoryId = async (category: string) => {
      const responsePostByCategory = await fetchPostByCategories([category])
      const mappedArticles =
        responsePostByCategory &&
        responsePostByCategory.data.map((item: any) => ({
          image1: item.attributes.image?.data?.attributes?.url || "",
          time: item.attributes.publishedAt ? new Date(item.attributes.publishedAt) : null,
          itemTitle: item.attributes.name || "Untitled",
          excerpt: item.attributes.excerpt?.replace(/<[^>]+>/g, "") || "",
          tags: item.attributes.tags.data || [],
        }))

      setArticleItems(mappedArticles || [])
      setFilteredArticles(mappedArticles || [])
      const allTags = mappedArticles.flatMap((item: ArticleItem) =>
        (item.tags || []).map((tag: TagData) => tag.attributes?.name || ""),
      )
      const uniqueTags: string[] = [...new Set(allTags)].filter(
        (tag): tag is string => typeof tag === "string" && tag.length > 0,
      )

      setTags(uniqueTags)
    }
    useEffect(() => {
      fetchPostByCategoryId(String(category))
    }, [category])

    useEffect(() => {
      if (activeTag === "All") {
        setFilteredArticles(articleItems)
      } else {
        const filtered = articleItems.filter(item => item.tags?.some(tag => tag.attributes.name === activeTag))
        setFilteredArticles(filtered)
      }
    }, [activeTag, articleItems])

    return (
      <>
        {responsiveCSS}
        <div id={id} className={twMerge(className, "w-full flex flex-col gap-10")}>
          <div className="flex flex-row gap-2">
            <button
              className={twMerge(
                "py-1.5 px-2.5 rounded-lg text-center",
                activeTag === "All" ? "bg-[#EBEDF0] text-[#242F3E] font-bold" : "bg-[#FFFFFF]",
              )}
              onClick={() => setActiveTag("All")}
            >
              All
            </button>
            {tags.map((tag, index) => (
              <button
                key={index}
                className={twMerge(
                  "py-1.5 px-2.5 rounded-lg text-center",
                  activeTag === tag ? "bg-[#EBEDF0] text-[#242F3E] font-bold" : "bg-[#FFFFFF]",
                )}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 ">
            {filteredArticles.map((item, index) => {
              return (
                <div key={index} className="flex flex-col gap-4 lg:max-w-[373.33px] max-w-[343px] lg:mb-6">
                  {item.image1 && (
                    <div className="relative h-[270px] lg:w-[373.33px] w-[343px] rounded-2xl overflow-hidden group">
                      <img
                        src={item.image1 ? generateImagePath(item.image1, "medium") : DefaultImage}
                        alt={item.itemTitle}
                        className={twMerge(
                          "h-full w-full object-cover rounded-2xl transition-transform duration-500 ease-in-out",
                          item.image2 ? "transition-[all] opacity-100 group-hover:opacity-0" : "group-hover:scale-110",
                        )}
                      />
                      {item.image2 && (
                        <img
                          src={item.image2 ? generateImagePath(item.image2, "medium") : DefaultImage}
                          alt={item.itemTitle}
                          className="absolute top-0 left-0 h-full w-full object-cover rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                        />
                      )}
                    </div>
                  )}

                  <div className="flex flex-row gap-2 items-center">
                    <div className="flex flex-wrap gap-2">
                      {item.tags &&
                        item.tags.map((tag, index) => (
                          <p key={index} className="py-1 px-2 rounded-md bg-[#EBEDF0] text-center text-[#4F4F4F]">
                            {tag.attributes.name}
                          </p>
                        ))}
                    </div>

                    {item.time && (
                      <p className="text-[#999999] font-normal text-sm" style={{ lineHeight: "19px" }}>
                        • {dayjs(item.time).format("MMM D, YYYY")}
                      </p>
                    )}
                  </div>
                  {item.itemTitle && (
                    <p className="font-semibold text-2xl text-[#242F3E]" style={{ lineHeight: "28px" }}>
                      {item.itemTitle}
                    </p>
                  )}
                  {item.excerpt && (
                    <p className="text-[#4F4F4F] text-base font-normal" style={{ lineHeight: "22px" }}>
                      {item.excerpt}
                    </p>
                  )}
                  <div className="flex flex-row w-full items-center justify-between pb-4 border-b border-[#EBEDF0]">
                    {item.time && (
                      <p className="text-[#999999] font-normal text-sm" style={{ lineHeight: "19px" }}>
                        {dayjs(item.time).format("DD/MM/YYYY")}
                      </p>
                    )}

                    <p
                      className="text-[#0B9444] font-normal text-sm hover:opacity-75 cursor-pointer duration-300"
                      style={{ lineHeight: "19px" }}
                    >
                      Read more
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </>
    )
  },
}
